import React from 'react';
import { Button, type ButtonProps } from '@chakra-ui/react';

interface InlineLinkButtonProps {
	children: React.ReactNode;
	onClick: () => void;
	/** Spacing is the sentence's business: a link that follows one is set off, a link inside one is not. */
	ml?: ButtonProps['ml'];
}

/**
 * A link that sits inside a sentence and does something, rather than going somewhere.
 *
 * Everything typographic is inherited — size, weight, colour, line height — so it reads as part of
 * the prose around it rather than as a control dropped into the middle of a paragraph. That is what
 * separates it from `AppLink`, which carries its own colour and sizing and is right for a link
 * standing on its own.
 *
 * A button, not an anchor: these run a handler in the page. An anchor with no href is not a control
 * to a keyboard or a screen reader.
 */
export const InlineLinkButton: React.FC<InlineLinkButtonProps> = (props: InlineLinkButtonProps) => {
	const { children, onClick, ml } = props;

	return (
		<Button
			type="button"
			variant="plain"
			size="xs"
			h="auto"
			minW={0}
			p={0}
			display="inline"
			verticalAlign="baseline"
			fontSize="inherit"
			fontWeight="semibold"
			lineHeight="inherit"
			color="inherit"
			textDecoration="underline"
			textUnderlineOffset="2px"
			ml={ml}
			cursor="pointer"
			_hover={{ color: 'brand.fg', opacity: 0.75 }}
			_focusVisible={{ outline: '2px solid', outlineColor: 'focus.ring', outlineOffset: '2px' }}
			onClick={onClick}
		>
			{children}
		</Button>
	);
};
